'use client'
import { Suspense, lazy, useEffect, useState } from 'react'
import { EventListener } from 'react-lottie'

const LazyLottieComponent = lazy(() => import('react-lottie') as any)

interface LottieComponentProps<T extends Record<string, unknown>> {
  getJson: () => Promise<T>
  id: string
  options?: Partial<{
    loop: boolean
    autoplay: boolean
    rendererSettings: Record<string, unknown>
  }>
  style?: React.CSSProperties
  isStopped?: boolean
  eventListeners?: EventListener[]
}

export function LazyLottie<T extends Record<string, unknown>>({
  getJson,
  id,
  options = {},
  style,
  isStopped,
  eventListeners,
}: Readonly<LottieComponentProps<T>>) {
  const [data, setData] = useState<T | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const abortController = new AbortController()

    async function fetchData() {
      try {
        const json = await getJson()
        setData(() => json)
      } finally {
        if (!abortController.signal.aborted) {
          setLoading(false)
        }
      }
    }

    if (typeof window !== 'undefined') {
      fetchData()
    }

    return () => abortController.abort()
  }, [getJson, id])

  if (loading) return null

  const mergedOptions = {
    loop: true,
    autoplay: true,
    animationData: data,
    rendererSettings: {
      preserveAspectRatio: 'xMidYMid slice',
    },
    ...options,
  }

  return (
    <Suspense fallback={null}>
      <LazyLottieComponent
        isStopped={isStopped}
        style={style}
        options={mergedOptions}
        eventListeners={eventListeners}
      />
    </Suspense>
  )
}
